import { Box } from "@mui/material";
import DataTable from "app/components/DataTable";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { selectCustomerPriceQuoteStatusLabelKey } from "../../../redux/selector";
import { CustomerPriceQuote, CustomerPriceQuoteStatus } from "../../../types";
import { useStyles } from "./styles";

type Props = {
  rows: CustomerPriceQuote[];
};

const getLocale = (language: string) =>
  language === "hw" || language === "he" ? "he-IL" : "en-US";

const formatCurrency = (value: number) => `$${value.toLocaleString()}`;

const formatDate = (value: string, language: string) =>
  new Intl.DateTimeFormat(getLocale(language), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(value));

const PriceQuotesTable = ({ rows }: Props) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();

  const getStatusClassName = (status: CustomerPriceQuoteStatus) => {
    switch (status) {
      case CustomerPriceQuoteStatus.DRAFT:
        return `${styles.statusChip} ${styles.draftChip}`;
      case CustomerPriceQuoteStatus.SENT:
        return `${styles.statusChip} ${styles.sentChip}`;
      case CustomerPriceQuoteStatus.ACCEPTED:
        return `${styles.statusChip} ${styles.acceptedChip}`;
      default:
        return styles.statusChip;
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "validUntil",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.VALID_UNTIL"),
        minWidth: 130,
        align: "right",
        format: (row: CustomerPriceQuote) =>
          formatDate(row.validUntil, i18n.language),
      },
      {
        id: "status",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.STATUS"),
        minWidth: 110,
        align: "right",
        format: (row: CustomerPriceQuote) => (
          <span className={getStatusClassName(row.status)}>
            {t(selectCustomerPriceQuoteStatusLabelKey(row.status))}
          </span>
        ),
      },
      {
        id: "totalValue",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.TOTAL_VALUE"),
        minWidth: 130,
        align: "right",
        format: (row: CustomerPriceQuote) => formatCurrency(row.totalValue),
      },
      {
        id: "revisionNumber",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.REVISION_NUMBER"),
        minWidth: 120,
        align: "center",
        format: (row: CustomerPriceQuote) => `${row.revisionNumber}.0`,
      },
      {
        id: "projectTypeKey",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.PROJECT_TYPE"),
        minWidth: 220,
        align: "right",
        format: (row: CustomerPriceQuote) => t(row.projectTypeKey),
      },
      {
        id: "createdAt",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.CREATED_AT"),
        minWidth: 140,
        align: "right",
        format: (row: CustomerPriceQuote) =>
          formatDate(row.createdAt, i18n.language),
      },
      {
        id: "quoteNumber",
        label: t("CUSTOMERS.DETAILS.PRICE_QUOTES.TABLE.QUOTE_NUMBER"),
        minWidth: 100,
        align: "right",
      },
    ],
    [i18n.language, styles, t]
  );

  return (
    <Box className={styles.root} dir={i18n.dir()}>
      <DataTable
        rows={rows}
        columns={columns}
        serialEnabled={false}
        noDataLabel={t("CUSTOMERS.DETAILS.PRICE_QUOTES.EMPTY")}
      />
    </Box>
  );
};

export default PriceQuotesTable;
